import React, { MutableRefObject, useEffect, useRef } from "react";
import { isNil } from "ramda";
import { css, styled } from "styled-components";

import { typeScale } from "lib/themeUtils";

const error = ({ $error }: { $error?: unknown }) =>
  $error
    ? css`
        border-color: ${(props) => props.theme.colours.error};
        background-color: #f7ebeb;

        &:hover {
          border-color: ${(props) => props.theme.colours.error};
        }

        &:focus {
          border-color: ${(props) => props.theme.colours.error};
        }
      `
    : css``;

export const InputElement = styled.input<{ name: string; $error?: unknown }>`
  ${typeScale.md}

  width: 100%;
  display: block;
  padding: 0.5rem 0.75rem;
  color: ${(props) => props.theme.colours.grey[900]};
  background: ${(props) => props.theme.colours.grey[50]};
  border: 1px solid ${(props) => props.theme.colours.grey[400]};
  border-radius: 6px;
  transition: all 200ms ease-in-out;

  &:hover {
    border-color: ${(props) => props.theme.colours.grey[600]};
  }

  &:focus {
    border-color: ${(props) => props.theme.colours.grey[600]};
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colours.focus};
  }

  &[type="number"] {
    /* stylelint-disable-next-line */
    -moz-appearance: textfield;
  }

  &[type="number"]::-webkit-inner-spin-button,
  &[type="number"]::-webkit-outer-spin-button {
    /* stylelint-disable-next-line */
    -webkit-appearance: none;
    margin: 0;
  }

  ${error}
`;

const ignoreScroll: EventListener = (e) => {
  if (e.target === document.activeElement) {
    e.preventDefault();
  }
};

export const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof InputElement>>(
  ({ name, $error, type, ...props }, ref) => {
    const innerRef = useRef<HTMLInputElement>(null);
    // eslint-disable-next-line no-prototype-builtins
    const elementRef = ref?.hasOwnProperty("current") ? ref : innerRef;

    useEffect(() => {
      const input = (elementRef as MutableRefObject<Maybe<HTMLInputElement> | null>).current;
      if (!isNil(input) && type === "number") {
        input.addEventListener("wheel", ignoreScroll);
      }
      return () => {
        if (!isNil(input) && type === "number") {
          input.removeEventListener("wheel", ignoreScroll);
        }
      };
    });
    return <InputElement name={name} $error={$error} type={type} ref={elementRef} {...props} />;
  }
);
Input.displayName = "Input";
